import { useState } from "react";
import { toast } from "sonner";
import { Calculator, FileText, Loader2, Mail } from "lucide-react";
import { api, errMsg, eur } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Field, PageTitle, Chip } from "../components/common";
import PrestationEditor from "../components/declare/PrestationEditor";
import { DateField } from "../components/DateFields";
import { QuoteHistory } from "../components/QuoteHistory";
import { MyTemplates } from "../components/MyTemplates";
import { QTY_FIELDS, SERVICE_TYPES, emptyComponent } from "../lib/labels";

const num = (v) => (v === "" || v == null ? 0 : Number(v));

const tpl = (type, zone, qty, special) => {
  const c = emptyComponent(type);
  return { ...c, zone, qty: { ...Object.fromEntries(QTY_FIELDS.map(([k]) => [k, 0])), ...qty }, special: { ...c.special, ...special } };
};
const TEMPLATES = [
  ["special", "Spécial hors Bruxelles (100 km, 1 h)", () => tpl("special", "", { del_pckgs: 1, del_stops: 1 }, { kind: "livraison", nature: "colis", km: 100, hours: 1, minutes: 0 })],
  ["fret", "Spécial frêt (4 palettes, 150 km, 1 h)", () => tpl("special_fret", "", { del_pallets: 4, del_pallet_stops: 1 }, { kind: "livraison", nature: "palette", km: 150, hours: 1, minutes: 0 })],
  ["express", "Express+ Bruxelles", () => tpl("express_plus", "Bruxelles", {}, {})],
];
const toComp = (c) => ({
  type: c.type, zone: c.zone, qty: Object.fromEntries(QTY_FIELDS.map(([k]) => [k, num(c.qty[k])])),
  special: ["special", "special_fret", "express_plus"].includes(c.type) ? { ...c.special, km: c.special.km === "" ? null : Number(c.special.km), photo_ids: [],
    minutes: c.special.hours === "" && c.special.minutes === "" ? null : Number(c.special.hours || 0) * 60 + Number(c.special.minutes || 0) } : null,
});

function SideResult({ title, side, testid }) {
  if (!side) return null;
  return (
    <div className="tl-card" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold uppercase">{title}</h3>
        <span className="text-2xl font-bold tl-mono" data-testid={`${testid}-total`}>{eur(side.known_total)}</span>
      </div>
      {side.status !== "complet" && <Chip tone="amber">À confirmer</Chip>}
      {(side.components || []).map((c, i) => (
        <div key={i} className="text-sm border-t pt-2 mt-2">
          <div className="font-semibold">{SERVICE_TYPES[c.type] || c.type}{c.rule_label ? ` · ${c.rule_label}` : ""}</div>
          {(c.lines || []).map((l, j) => <div key={j} className="flex justify-between text-slate-600"><span>{l.basis} × {l.qty} @ {l.price}</span><span className="tl-mono">{eur(l.amount)}</span></div>)}
          {(c.missing || []).map((m, j) => <div key={j} className="text-amber-700 text-xs">{m}</div>)}
          {c.error && <div className="text-red-700 text-xs">{c.error}</div>}
        </div>
      ))}
    </div>
  );
}

function QuoteBox({ payload, onCreated }) {
  const [q, setQ] = useState({ email: "", recipient_name: "", note: "" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post("/quotes", { ...payload(), ...q }, { responseType: "blob" });
      const ref = r.headers["x-quote-ref"] || "devis";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `${ref}.pdf`;
      a.click();
      toast.success(q.email ? `Devis ${ref} envoyé à ${q.email} et téléchargé` : `Devis ${ref} téléchargé`);
      onCreated();
    } catch (e) {
      const txt = e.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : errMsg(e);
      toast.error(txt || "Erreur");
    }
    setBusy(false);
  };
  return (
    <div className="tl-card space-y-3" data-testid="quote-box">
      <h3 className="font-bold uppercase flex items-center gap-2"><FileText size={18} /> Transformer en devis PDF</h3>
      <p className="text-xs text-slate-500">Le devis affiche uniquement le prix sous-traitant.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="E-mail du destinataire (optionnel)"><input type="email" className="tl-input" value={q.email} onChange={(e) => setQ({ ...q, email: e.target.value })} data-testid="quote-email" /></Field>
        <Field label="Nom du destinataire (optionnel)"><input className="tl-input" value={q.recipient_name} onChange={(e) => setQ({ ...q, recipient_name: e.target.value })} data-testid="quote-name" /></Field>
      </div>
      <Field label="Remarque (optionnel)"><textarea className="tl-textarea" value={q.note} onChange={(e) => setQ({ ...q, note: e.target.value })} data-testid="quote-note" /></Field>
      <button className="tl-btn tl-btn-dark" onClick={send} disabled={busy} data-testid="quote-send">{busy ? <Loader2 className="animate-spin" size={16} /> : <Mail size={16} />} {q.email ? "Créer, envoyer et télécharger" : "Créer et télécharger"}</button>
    </div>
  );
}

export default function Simulation() {
  const { user } = useAuth();
  const staff = user.role !== "soustraitant";
  const [workDate, setWorkDate] = useState(new Date().toISOString().slice(0, 10));
  const [p, setP] = useState({ mixed: false, observations: "", components: [emptyComponent()] });
  const [res, setRes] = useState(null);
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const payload = (comps = p.components) => ({ work_date: workDate, components: comps.map(toComp) });
  const applyTemplate = (make) => {
    const next = { mixed: false, observations: "", components: [make()] };
    setP(next);
    run(next.components);
  };
  const applySaved = (components) => {
    const next = { mixed: components.length > 1, observations: "", components };
    setP(next);
    run(components);
  };
  const run = async (comps) => {
    setBusy(true);
    try {
      const { data } = await api.post("/simulate", payload(Array.isArray(comps) ? comps : p.components));
      setRes(data);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-5" data-testid="simulation-page">
      <PageTitle title="Simulation des prix" sub="Le minimum pour connaître le prix d'un service. La simulation n'enregistre rien ; seuls les devis et vos modèles sont conservés." />
      <div className="tl-card flex flex-wrap gap-4 items-end">
        <Field label="Date du service" className="w-56"><DateField value={workDate} onChange={setWorkDate} testid="sim-date" /></Field>
        <div className="flex-1 min-w-[260px]">
          <span className="tl-label">Devis rapides (modèles prêts à l'emploi)</span>
          <div className="flex flex-wrap gap-2">
            {TEMPLATES.map(([k, l, make]) => <button key={k} className="tl-btn tl-btn-ghost tl-btn-sm" onClick={() => applyTemplate(make)} data-testid={`sim-template-${k}`}>{l}</button>)}
          </div>
        </div>
        <div className="basis-full"><MyTemplates current={() => p.components} onApply={applySaved} /></div>
      </div>
      <PrestationEditor p={p} pi={0} onChange={setP} onRemove={null} />
      <button className="tl-btn tl-btn-primary" onClick={run} disabled={busy} data-testid="sim-run">{busy ? <Loader2 className="animate-spin" size={16} /> : <Calculator size={16} />} Simuler</button>
      {res && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="sim-result">
          <SideResult title="Facturé UPS" side={res.ups} testid="sim-ups" />
          <SideResult title={staff ? "Dû au sous-traitant" : "Votre prix"} side={res.sub} testid="sim-sub" />
          {res.ups && res.sub && <div className="tl-card md:col-span-2 font-bold" data-testid="sim-margin">Marge : {eur((res.ups.known_total || 0) - (res.sub.known_total || 0))}</div>}
        </div>
      )}
      {res && <QuoteBox payload={payload} onCreated={() => setTick((x) => x + 1)} />}
      <QuoteHistory tick={tick} />
    </div>
  );
}
